import React, { useState, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from '../forms.module.css';
import collapseStyles from './contactTabsSection.module.css';
import { TCompanyLabeledOption } from '../../../services/types/users.types';
import { TContactPerson } from '../../../services/types/user.types';
import ContactDetailsSection from '../contactDetailsSection/contactDetailsSection';
import DeviceContactSection from '../deviceContactSection/deviceContactSection';
import Modal from '../../modals/modal/Modal';
import DeleteConfirmation from '../../modals/deleteConfirmation/deleteConfirmation';

interface ContactTabsSectionProps {
  companiesOptions: TCompanyLabeledOption[];
  genders: Array<{ value: number; label: string | number }>;
  onClose: () => void;
  handleChange: (index: number, field: string, value: any) => void;
  usersRoleId?: number;
  onAddContact: () => void;
  contactMode?: 'person' | 'device';
}

function ContactTabsSection({
  companiesOptions,
  genders,
  onClose,
  handleChange,
  usersRoleId,
  onAddContact,
  contactMode = 'person',
}: ContactTabsSectionProps) {
  const { values, setFieldValue } = useFormikContext<{ contacts: TContactPerson[] }>();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  // Folded by default so a long contact list does not push the rest of the form down.
  const [isOpen, setIsOpen] = useState(false);
  // The tab whose removal is waiting for the admin to confirm it.
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);
  const isDeviceMode = contactMode === 'device';

  const contacts = values.contacts ?? [];

  useEffect(() => {
    if (contacts.length > 0) {
      setActiveTabIndex(contacts.length - 1);
    }
  }, [contacts.length]);

  const handleRemoveContact = (indexToRemove: number) => {
    const newContacts = contacts.filter((_, i) => i !== indexToRemove);
    setFieldValue('contacts', newContacts);

    if (activeTabIndex >= newContacts.length) {
      setActiveTabIndex(Math.max(0, newContacts.length - 1));
    } else if (activeTabIndex > indexToRemove) {
      setActiveTabIndex(activeTabIndex - 1);
    }
  };

  const handleAddContact = () => {
    setIsOpen(true);
    onAddContact();
  };

  const getContactTabLabel = (index: number) => {
    const contact = contacts[index];
    if (isDeviceMode) {
      if (contact?.firstName) {
        return contact.firstName.trim();
      }
      return `${t('DEVICE')} ${index + 1}`;
    }
    if (contact?.firstName || contact?.lastName) {
      return `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
    }
    return usersRoleId === -2
      ? `${t('PATIENT_CONTACT')} ${index + 1}`
      : `${t('CONTACT_PERSON')} ${index + 1}`;
  };

  const emptyIcon = isDeviceMode ? '🔌' : '👤';
  const emptyText = isDeviceMode
    ? t('NO_DEVICES_YET')
    : usersRoleId === -2
      ? t('NO_PATIENT_CONTACTS_YET')
      : t('NO_CONTACTS_YET');
  const addLabel = isDeviceMode
    ? t('ADD_DEVICE')
    : usersRoleId === -2
      ? t('ADD_PATIENT_CONTACT')
      : t('ADD_CONTACT');
  const sectionTitle = isDeviceMode
    ? t('STAFF_DEVICES')
    : usersRoleId === -2
      ? t('LINKED_PATIENTS')
      : t('PATIENT_CONTACTS');

  const toggleBar = (
    <button
      type="button"
      className={collapseStyles.toggle}
      aria-expanded={isOpen}
      onClick={() => setIsOpen((open) => !open)}
    >
      <span className={styles.contactTabsTitleIcon}>{isDeviceMode ? '🔌' : '👥'}</span>
      <span className={collapseStyles.toggle__title}>{sectionTitle}</span>
      {contacts.length > 0 && <span className={collapseStyles.toggle__count}>{contacts.length}</span>}
      <span className={`${collapseStyles.toggle__chevron} ${isOpen ? collapseStyles.toggle__chevron_open : ''}`} />
    </button>
  );

  if (contacts.length === 0) {
    return (
      <section className={collapseStyles.collapsible}>
      {toggleBar}
      {isOpen && (
      <div className={`${styles.contactTabsEmptyState} ${collapseStyles.body}`}>
        <div className={styles.contactTabsEmptyStateContent}>
          <div className={styles.contactTabsEmptyIcon}>{emptyIcon}</div>
          <p>{emptyText}</p>
        </div>
        <button
          type="button"
          className={styles.contactTabsEmptyStateButton}
          onClick={handleAddContact}
        >
          {addLabel}
        </button>
      </div>
      )}
      </section>
    );
  }

  return (
    <section className={collapseStyles.collapsible}>
    {toggleBar}
    {isOpen && (
    <div className={`${styles.contactTabsContainer} ${collapseStyles.body}`}>
      {pendingRemoveIndex !== null && (
        <Modal onClose={() => setPendingRemoveIndex(null)}>
          <DeleteConfirmation
            text={t('REMOVE_CONTACT_CONFIRM', { name: getContactTabLabel(pendingRemoveIndex) })}
            warningDetail={t('REMOVE_CONTACT_SAVE_HINT')}
            btnText={t('REMOVE_CONTACT')}
            onDelete={() => handleRemoveContact(pendingRemoveIndex)}
            onClose={() => setPendingRemoveIndex(null)}
          />
        </Modal>
      )}
      <div className={styles.contactTabsHeader}>
        <ul className={styles.contactTabsList}>
          {contacts.map((_, index) => (
            <li key={index}>
              <button
                type="button"
                className={`${styles.contactTab} ${activeTabIndex === index ? styles.contactTab_active : ''}`}
                onClick={() => setActiveTabIndex(index)}
              >
                <span className={styles.contactTabIcon}>{index + 1}</span>
                <span>{getContactTabLabel(index)}</span>
                <span
                  className={styles.contactTabRemove}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingRemoveIndex(index);
                  }}
                  title={t('REMOVE_CONTACT')}
                >
                  ×
                </span>
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className={styles.contactTabAdd}
          onClick={handleAddContact}
          title={addLabel}
        >
          <span>+</span>
        </button>
      </div>

      <div className={styles.contactTabContent}>
        {contacts.length > 0 && activeTabIndex < contacts.length && (
          isDeviceMode ? (
            <DeviceContactSection
              key={activeTabIndex}
              index={activeTabIndex}
              companiesOptions={companiesOptions}
              handleChange={handleChange}
            />
          ) : (
            <ContactDetailsSection
              key={activeTabIndex}
              index={activeTabIndex}
              genders={genders}
              onRemove={() => setPendingRemoveIndex(activeTabIndex)}
              usersRoleId={usersRoleId}
              handleChange={handleChange}
              companiesOptions={companiesOptions}
              onClose={onClose}
              hideHeader={true}
            />
          )
        )}
      </div>
    </div>
    )}
    </section>
  );
}

export default ContactTabsSection;
